import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  IconButton,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import WifiIcon from "@mui/icons-material/Wifi";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode";

const WiFiConfigModal = ({ open, onClose, spaceData }) => {
  const { t } = useTranslation();
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  const wifiData = spaceData?.wifi;

  // Generar QR Code cuando se abra el modal y haya datos WiFi
  useEffect(() => {
    if (open && wifiData?.ssid && wifiData?.password) {
      generateQRCode();
    } else {
      setQrCodeUrl(null);
    }
  }, [open, wifiData]);

  const generateQRCode = async () => {
    try {
      // Función para escapar caracteres especiales en SSID y password
      // iOS requiere que los caracteres especiales estén escapados con backslash
      const escapeWiFiString = (str) => {
        if (!str) return "";
        // Escapar: \, ;, :, ,, "
        return str.replace(/\\/g, "\\\\")
                  .replace(/;/g, "\\;")
                  .replace(/:/g, "\\:")
                  .replace(/,/g, "\\,")
                  .replace(/"/g, "\\\"");
      };

      // Normalizar el tipo de seguridad para el formato WiFi QR
      // Valores válidos según estándar: WPA, WPA2, WEP, nopass
      let security = (wifiData.security || "WPA2").toUpperCase();
      // Validar que sea un valor válido
      if (!["WPA", "WPA2", "WEP", "NOPASS"].includes(security)) {
        security = "WPA2";
      }
      
      // Limpiar y escapar SSID y password (trim primero para evitar espacios)
      const ssid = escapeWiFiString(String(wifiData.ssid || "").trim());
      const password = escapeWiFiString(String(wifiData.password || "").trim());
      
      if (!ssid) {
        console.error("SSID vacío, no se puede generar QR");
        setQrCodeUrl(null);
        return;
      }
      
      // Construir el string WiFi según el estándar (el orden de campos importa)
      // Formato estándar: WIFI:T:<TIPO>;S:<SSID>;P:<PASSWORD>;;
      const wifiString = `WIFI:T:${security};S:${ssid};P:${password};;`;
      
      // Debug: mostrar el string generado (ocultar contraseña)
      console.log("WiFi QR String:", wifiString.replace(/P:[^;]+/, "P:***"));
      
      // Generar QR Code optimizado para móviles (especialmente iOS)
      const qrDataUrl = await QRCode.toDataURL(wifiString, {
        width: 400, // Mayor tamaño para mejor lectura en móviles
        margin: 4,  // Mayor margen para mejor detección
        errorCorrectionLevel: 'H', // Alto nivel de corrección para mejor lectura
        color: {
          dark: "#000000",
          light: "#FFFFFF",
        },
      });
      
      setQrCodeUrl(qrDataUrl);
    } catch (error) {
      console.error("Error al generar QR Code:", error);
      setQrCodeUrl(null);
    }
  };

  const handleCopyPassword = async () => {
    if (wifiData?.password) {
      try {
        await navigator.clipboard.writeText(wifiData.password);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        console.error("Error al copiar:", error);
        // Fallback: seleccionar texto
        const textField = document.createElement("textarea");
        textField.value = wifiData.password;
        document.body.appendChild(textField);
        textField.select();
        document.execCommand("copy");
        document.body.removeChild(textField);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  if (!wifiData || !wifiData.ssid || !wifiData.password) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WifiIcon sx={{ color: "#66bc5f" }} />
            <Typography variant="h6">{t("wifi.title")}</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* QR Code */}
          {qrCodeUrl && (
            <Box sx={{ textAlign: "center"}}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t("wifi.qrCodeDescription")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  p: 1,
                  backgroundColor: "#fff",
                  borderRadius: 2,
                  border: "1px solid #e0e0e0",
                  mt: 1,
                }}
              >
                <img
                  src={qrCodeUrl}
                  alt="WiFi QR Code"
                  style={{
                    maxWidth: "100%",
                    height: "auto",
                    maxHeight: "300px",
                  }}
                />
              </Box>
            </Box>
          )}
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t("wifi.networkName")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: "0.8rem", paddingTop: "6px" }}>
                {wifiData.ssid || ""}
              </Typography>
            </Box>

            <Box sx={{ flex: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t("wifi.password")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, fontSize: "0.8rem" }}>
                  {wifiData.password || ""}
                </Typography>
                <IconButton
                  onClick={handleCopyPassword}
                  size="small"
                  color={copied ? "success" : "default"}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>

              {copied && (
                <Typography variant="caption" color="success.main">
                  {t("wifi.passwordCopied")}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={onClose} 
          variant="contained" 
          sx={{
            backgroundColor: "#66bc5f",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#5aa954",
            },
          }}
        >
          {t("common.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WiFiConfigModal;

